import { pointInPolygon, polygonAreaPlanar } from "@energyos/geometry-core";
const DEG = Math.PI / 180;
/**
 * Verschiebungsvektor (in Metern), um den ein um `dh` Meter erhöhter Punkt bei
 * gegebenem Sonnenstand auf die Ebene projiziert wird. Der Schatten fällt von der
 * Sonne weg (Azimut + 180°). Bei Elevation ≤ 0 (Sonne unter Horizont) gibt es
 * keinen endlichen Wurf → null.
 *
 * Konvention: Azimut θ im Uhrzeigersinn ab Nord. Schattenrichtung = θ + 180°,
 * Ost-Komponente = sin(θ+180) = −sin(θ), Nord-Komponente = cos(θ+180) = −cos(θ).
 */
function shadowOffset(sun, dh) {
    if (!(sun.elevationDeg > 0))
        return null;
    if (!(dh > 0))
        return { x: 0, y: 0 };
    const L = dh / Math.tan(sun.elevationDeg * DEG); // horizontale Wurflänge (m)
    const a = sun.azimuthDeg * DEG;
    // Schatten zeigt von der Sonne weg:
    return { x: -Math.sin(a) * L, y: -Math.cos(a) * L };
}
/**
 * (1) Projizierter Schattenbereich des Hindernisses auf die Ebene für einen
 * Sonnenstand.
 *
 * Jeder Footprint-Eckpunkt liefert seinen Bodenpunkt (Basis) UND seinen
 * projizierten Oberkantenpunkt (Basis + Offset(heightM)). Die konvexe Hülle
 * dieser 2·N Punkte ist der Schattenwurf inkl. überdecktem Footprint — eine
 * für die Modul-Überlappung ausreichende, konservative Näherung (genau für
 * konvexe Footprints, leichte Überdeckung bei konkaven).
 *
 * Elevation ≤ 0 → degeneriertes (leeres) Polygon `[]`.
 */
export function shadowPolygon(obstacle, sun) {
    const fp = obstacle.footprint;
    if (fp.length < 1)
        return [];
    const base = obstacle.baseHeightM ?? 0;
    const topDh = obstacle.heightM - base; // wurfrelevante Höhe der Oberkante
    if (!(topDh > 0))
        return []; // kein erhabenes Hindernis → kein Schatten
    const off = shadowOffset(sun, topDh);
    if (off === null)
        return []; // Sonne unter/auf Horizont → kein endlicher Schatten
    const pts = [];
    for (const p of fp) {
        pts.push({ x: p.x, y: p.y }); // Bodenpunkt (Basis-Projektion bei baseHeight 0)
        pts.push({ x: p.x + off.x, y: p.y + off.y }); // projizierte Oberkante
    }
    return convexHull(pts);
}
/**
 * (2) Modulfreie Zone aus dem Hindernis-Footprint: der Footprint um `clearanceM`
 * nach außen versetzt (gepuffert). Für gridLayoutPlanar als Keepout nutzbar.
 *
 * Näherung: jeder Eckpunkt wird um `clearanceM` entlang seiner nach-außen
 * gerichteten Eckhalbierenden verschoben (Miter-Offset). Für konvexe Footprints
 * exakt im Sinne eines umlaufenden Mindestabstands; bei `clearanceM ≤ 0` wird
 * der Footprint unverändert (kopiert) zurückgegeben.
 */
export function keepoutFromObstacle(obstacle, clearanceM) {
    const fp = obstacle.footprint;
    if (fp.length < 3)
        return fp.map((p) => ({ x: p.x, y: p.y }));
    if (!(clearanceM > 0))
        return fp.map((p) => ({ x: p.x, y: p.y }));
    const n = fp.length;
    // Orientierung bestimmen (CCW/CW), damit "außen" eindeutig ist.
    const ccw = signedArea(fp) > 0;
    const out = [];
    for (let i = 0; i < n; i++) {
        const prev = fp[(i - 1 + n) % n];
        const cur = fp[i];
        const next = fp[(i + 1) % n];
        // Nach-außen-Normalen der beiden anliegenden Kanten (Einheitslänge).
        const nIn = outwardNormal(prev, cur, ccw);
        const nOut = outwardNormal(cur, next, ccw);
        // Eckhalbierende = normierte Summe der beiden Kantennormalen.
        let bx = nIn.x + nOut.x;
        let by = nIn.y + nOut.y;
        const blen = Math.hypot(bx, by);
        if (blen < 1e-9) {
            // Gegenläufige Normalen (180°-Knick) → eine Normale nehmen.
            bx = nOut.x;
            by = nOut.y;
        }
        else {
            bx /= blen;
            by /= blen;
        }
        // Miter-Verlängerung: Versatz entlang der Halbierenden so, dass beide Kanten
        // um clearanceM nach außen rücken. Faktor = 1 / cos(halber Innenwinkel).
        const cos = bx * nOut.x + by * nOut.y;
        const scale = cos > 1e-6 ? clearanceM / cos : clearanceM;
        out.push({ x: cur.x + bx * scale, y: cur.y + by * scale });
    }
    return out;
}
/**
 * (3) Anteil (0..1) eines achsparallelen Modul-Rechtecks, der vom Schatten-
 * polygon überdeckt wird. Näherung per regelmäßigem Sample-Raster über das
 * Rechteck (Zellmittelpunkte): robust auch für konkave/komplexe Schattenformen
 * und ohne Polygon-Clipping. Auflösung skaliert mit der Rechteckgröße.
 *
 * Leeres/degeneriertes Schattenpolygon oder Rechteck → 0.
 */
export function moduleShadedFraction(moduleRect, shadowPoly) {
    const { xM, yM, wM, hM } = moduleRect;
    if (!(wM > 0) || !(hM > 0))
        return 0;
    if (shadowPoly.length < 3)
        return 0;
    if (polygonAreaPlanar(shadowPoly) <= 0)
        return 0;
    // Rasterauflösung: ~ alle 5 cm, gedeckelt auf [8, 40] Zellen je Achse.
    const cellM = 0.05;
    const nx = clampInt(Math.round(wM / cellM), 8, 40);
    const ny = clampInt(Math.round(hM / cellM), 8, 40);
    let inside = 0;
    for (let j = 0; j < ny; j++) {
        const py = yM + ((j + 0.5) / ny) * hM;
        for (let i = 0; i < nx; i++) {
            const px = xM + ((i + 0.5) / nx) * wM;
            if (pointInPolygon({ x: px, y: py }, shadowPoly))
                inside++;
        }
    }
    return inside / (nx * ny);
}
// ---------------------------------------------------------------------------
// interne Helfer
// ---------------------------------------------------------------------------
function clampInt(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}
/** Vorzeichenbehaftete Fläche (Shoelace): > 0 = CCW, < 0 = CW. */
function signedArea(poly) {
    let s = 0;
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        s += a.x * b.y - b.x * a.y;
    }
    return s / 2;
}
/** Einheits-Außennormale der gerichteten Kante (a → b) für gegebene Orientierung. */
function outwardNormal(a, b, ccw) {
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len = Math.hypot(ex, ey) || 1;
    // Rechte Normale (ey, -ex) zeigt bei CCW nach außen, bei CW nach innen.
    let nx = ey / len;
    let ny = -ex / len;
    if (!ccw) {
        nx = -nx;
        ny = -ny;
    }
    return { x: nx, y: ny };
}
/** Konvexe Hülle (Andrew's monotone chain), CCW, ohne doppelten Schlusspunkt. */
function convexHull(points) {
    const pts = points
        .slice()
        .sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
    // Duplikate entfernen (numerisch nah).
    const uniq = [];
    for (const p of pts) {
        const last = uniq[uniq.length - 1];
        if (!last || Math.abs(last.x - p.x) > 1e-12 || Math.abs(last.y - p.y) > 1e-12) {
            uniq.push(p);
        }
    }
    if (uniq.length < 3)
        return uniq;
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower = [];
    for (const p of uniq) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper = [];
    for (let i = uniq.length - 1; i >= 0; i--) {
        const p = uniq[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}
//# sourceMappingURL=shadow.js.map